import React from 'react';
import { useMemoIndexStore } from '../../stores/memoIndexStore';
import { UIMemoIndex } from '../../model/UiShapes';
import { memoIndexKeys } from '../api/memoIndexAPI';
import { useMemoIndexListQuery, useMemoIndexQuery, useRefreshAllMemoIndexesMutation, useRefreshMemoIndexByGuidMutation } from '../query/memoIndexQuery';
import { useAsyncOperations } from './asyncCommon';

// Store-based hooks for direct store access
export const useMemoIndexStoreData = () => {
  const memoIndexes = useMemoIndexStore((state) => state.memoIndexes);
  const currentMemoIndex = useMemoIndexStore((state) => state.currentMemoIndex);
  const selectedTag = useMemoIndexStore((state) => state.selectedTag);
  const status = useMemoIndexStore((state) => state.status);
  const error = useMemoIndexStore((state) => state.error);
  const lastUpdated = useMemoIndexStore((state) => state.lastUpdated);
  const labelToMemoGuidMap = useMemoIndexStore((state) => state.labelToMemoGuidMap);
  
  return {
    memoIndexes,
    currentMemoIndex,
    selectedTag,
    status,
    error,
    lastUpdated,
    labelToMemoGuidMap
  };
};

// 综合管理hook，结合React Query和Zustand
export const useMemoIndexManager = (memoId?: string) => {
  const storeData = useMemoIndexStoreData();
  const storeActions = useMemoIndexStore.getState();
  const { safeAsyncOperation, queryClient } = useAsyncOperations();
  
  const { 
    data: memoIndexesData, 
    isLoading: memoIndexesLoading, 
    error: memoIndexesError 
  } = useMemoIndexListQuery();
  
  const { 
    data: memoIndexData, 
    isLoading: memoIndexLoading, 
    error: memoIndexError 
  } = useMemoIndexQuery(memoId || '');
  
  const { 
    mutate: refreshAllMemoIndexes, 
    isPending: isRefreshingAll 
  } = useRefreshAllMemoIndexesMutation();
  
  const { 
    mutate: refreshMemoIndexByGuid, 
    isPending: isRefreshingByGuid 
  } = useRefreshMemoIndexByGuidMutation();
  
  // 数据同步逻辑：将React Query数据同步到Zustand store
  React.useEffect(() => {
    if (memoIndexesData && !memoIndexesLoading) {
      // 只有当store中没有数据或数据不同步时才更新
      if (storeData.memoIndexes.length === 0 || JSON.stringify(storeData.memoIndexes) !== JSON.stringify(memoIndexesData)) {
        storeActions.setMemoIndexes(memoIndexesData);
        storeActions.setStatus('idle');
      }
    }
  }, [memoIndexesData, memoIndexesLoading, storeData.memoIndexes]);
  
  // 当前memo index数据同步
  React.useEffect(() => {
    if (memoIndexData && !memoIndexLoading && memoId) {
      // 只有当store中无数据时才用React Query数据初始化store，避免覆盖乐观更新
      const currentStoreData = useMemoIndexStore.getState().currentMemoIndex;
      if (!currentStoreData || currentStoreData.guid !== memoId) {
        storeActions.setCurrentMemoIndex(memoIndexData);
      }
    }
  }, [memoIndexData, memoIndexLoading, memoId]);
  
  // 处理错误状态同步
  React.useEffect(() => {
    if (memoIndexesError) {
      storeActions.setStatus('failed');
      storeActions.setError(memoIndexesError.message);
    }
  }, [memoIndexesError]);
  
  React.useEffect(() => {
    if (memoIndexError) {
      storeActions.setStatus('failed');
      storeActions.setError(memoIndexError.message);
    }
  }, [memoIndexError]);
  
  // 智能错误处理
  const hasError = memoIndexesError || memoIndexError;
  
  // 获取当前memo的index
  const getMemoIndex = React.useCallback(() => {
    if (!memoId) return null;

    // 优先使用Zustand store中的当前memo index
    if (storeData.currentMemoIndex?.guid === memoId) return storeData.currentMemoIndex;

    // 其次使用React Query缓存数据
    if (memoIndexData) return memoIndexData;

    // 然后从memoIndexes列表中查找
    const cachedIndex = storeData.memoIndexes.find(index => index.guid === memoId);
    if (cachedIndex) return cachedIndex;

    return null;
  }, [memoId, storeData.currentMemoIndex, memoIndexData, storeData.memoIndexes]);
  
  const memoIndex = React.useMemo(() => getMemoIndex(), [getMemoIndex]);
  
  // Integration actions实现 - 使用统一异步操作封装
  const refreshAllIndexesWithSync = async () => {
    const result = await safeAsyncOperation(async () => {
      await refreshAllMemoIndexes();
      return { success: true };
    }, '刷新所有memo indexes失败');
    
    return result.success;
  };
  
  const refreshIndexByGuidWithSync = async (targetMemoId: string) => {
    const result = await safeAsyncOperation(async () => {
      await refreshMemoIndexByGuid(targetMemoId);
      return { success: true };
    }, `刷新memoId为"${targetMemoId}"的memo index失败`);
    
    return result.success;
  };
  
  const forceRefreshMemoIndexData = async () => {
    const result = await safeAsyncOperation(async () => {
      storeActions.setStatus('loading');
      storeActions.clearError();
      
      // 使用React Query强制刷新数据
      const [listData, detailData] = await Promise.all([
        queryClient.refetchQueries({ queryKey: memoIndexKeys.list() }),
        memoId ? queryClient.refetchQueries({ queryKey: memoIndexKeys.detail(memoId) }) : Promise.resolve(null)
      ]);
      
      // 获取最新的数据
      const latestListData = queryClient.getQueryData(memoIndexKeys.list()) as UIMemoIndex[];
      const latestDetailData = memoId ? queryClient.getQueryData(memoIndexKeys.detail(memoId)) as UIMemoIndex : null;
      
      if (latestListData && Array.isArray(latestListData)) {
        // 更新store状态
        storeActions.setMemoIndexes(latestListData);
        storeActions.setStatus('idle');
      }
      
      if (latestDetailData) {
        // 更新当前memo index状态
        storeActions.forceRefreshMemoIndexByMemoId(latestDetailData);
      } else if (memoId) {
        // 如果没有数据，清除对应的缓存
        storeActions.forceRefreshMemoIndexByMemoId(null);
      }
      
      return { success: true };
    }, '强制刷新memo index数据失败');
    
    return result.success;
  };
  
  const syncAllMemoIndexData = async () => {
    const result = await safeAsyncOperation(async () => {
      await queryClient.refetchQueries({ queryKey: memoIndexKeys.list() });
      if (memoId) {
        await queryClient.refetchQueries({ queryKey: memoIndexKeys.detail(memoId) });
      }
      return { success: true };
    }, '同步所有memo index数据失败');
    
    return result.success;
  };
  
  return {
    // Store状态
    ...storeData,
    
    // React Query状态
    memoIndexesData,
    memoIndexesLoading,
    memoIndexesError,
    memoIndexData,
    memoIndexLoading,
    memoIndexError,
    
    // 组合状态
    memoIndex,
    hasMemoIndexes: storeData.memoIndexes.length > 0,
    isMemoIndexSelected: !!storeData.selectedTag,
    isMemoIndexLoading: memoIndexesLoading || memoIndexLoading || isRefreshingAll || isRefreshingByGuid || storeData.status === 'loading',
    
    // Error states
    hasError,
    
    // Store操作
    storeActions,
    
    // Integration actions
    refreshAllIndexes: refreshAllIndexesWithSync,
    refreshIndexByGuid: refreshIndexByGuidWithSync,
    forceRefresh: forceRefreshMemoIndexData,
    syncAllData: syncAllMemoIndexData,
    
    // 智能状态判断
    canRefreshAll: !memoIndexesLoading && !hasError,
    canRefreshIndex: !!memoId && !memoIndexLoading && !hasError,
    
    // 数据同步状态
    isDataSynced: () => {
      return !memoIndexesData || (storeData.memoIndexes.length === memoIndexesData.length);
    }
  };
};

/**
 * 优化后的memo index获取hook - 保持向后兼容
 */
export const useOptimizedFetchAllMemoIndexes = useMemoIndexListQuery;
export const useOptimizedFetchMemoIndexByGuid = useMemoIndexQuery;

export const useRefreshAllMemoIndexes = useRefreshAllMemoIndexesMutation;
export const useRefreshMemoIndexByGuid = useRefreshMemoIndexByGuidMutation;

// 导出store actions获取函数
export const useMemoIndexStoreActions = () => {
  const state = useMemoIndexStore.getState();
  // 添加安全保护，确保返回有效对象
  const actions = state || {};
  return actions || {};
};